import React, { useState, useEffect } from "react";
import { Button, Input, Separator } from "@postcheesy/ui-lib/atoms";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Form, FormControl, FormItem, FormLabel, FormMessage, Toaster, useToast } from "@postcheesy/ui-lib/molecules";
import { useForm } from "react-hook-form";
import axios from "axios";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Layout } from "../../components/layouts/layout";
import { Container } from "../../components/layouts/container";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { LogoutButton } from "../../components/ui/LogoutButton";

const resetPasswordSchema = z.object({
    newPassword: z.string()
        .min(8, "*Le mot de passe doit contenir au moins 8 caractères.")
        .regex(/[a-z]/, "*Le mot de passe doit contenir au moins une lettre minuscule.")
        .regex(/[A-Z]/, "*Le mot de passe doit contenir au moins une lettre majuscule.")
        .regex(/[0-9]/, "*Le mot de passe doit contenir au moins un chiffre.")
        .regex(/[\W_]/, "*Le mot de passe doit contenir au moins un caractère spécial."),
    confirmPassword: z.string()
        .min(8, "*Le mot de passe doit contenir au moins 8 caractères."),
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: "*Les mots de passe ne correspondent pas.",
    path: ["confirmPassword"],
});

export const ResetPassword: React.FC = () => {
    const { toast } = useToast();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const resetPasswordForm = useForm<z.infer<typeof resetPasswordSchema>>({
        resolver: zodResolver(resetPasswordSchema),
        mode: "onBlur",
    });

    const handleResetPassword = async (data: z.infer<typeof resetPasswordSchema>) => {
        try {
            const apiUrl = import.meta.env.VITE_API_URL;
            const urlParams = new URLSearchParams(window.location.search);
            const resetCode = urlParams.get("code");
            const email = urlParams.get("email");

            if (!email || !resetCode) {
                toast({
                    title: "Erreur",
                    description: "L'e-mail ou le code de réinitialisation est manquant.",
                    variant: "destructive",
                });
                return;
            }

            const requestData = {
                email: email.trim(),
                resetCode: resetCode.trim(),
                newPassword: data.newPassword,
            };

            await axios.post(`${apiUrl}/resetPassword`, requestData, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 10000,
            });

            toast({
                title: "Succès",
                description: "Votre mot de passe a été réinitialisé avec succès. Connectez-vous avec votre nouveau mot de passe.",
                variant: "default",
            });
            setShowSuccess(true);

        } catch (error) {
            if (axios.isAxiosError(error)) {
                toast({
                    title: "Erreur",
                    description: error.response?.data?.message || "Échec de la réinitialisation. Vérifiez votre code de réinitialisation et réessayez.",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Erreur",
                    description: "Une erreur inattendue s'est produite.",
                    variant: "destructive",
                });
            }
        }
    };

    return (
        <Layout>
            <Toaster />
            <Container>
                <div className="relative mx-auto grid max-h-full w-fit grid-cols-1 items-start justify-center">
                    <section className="lg:my-18 container mx-auto my-12 h-fit max-w-xl py-8 md:my-12 2xl:my-32">
                        <LogoutButton variant="link">{"< Retourner à la page de connexion"}</LogoutButton>
                        <Card className="w-full md:min-w-96">
                            <CardHeader>
                                <CardTitle>Réinitialiser votre mot de passe</CardTitle>
                            </CardHeader>
                            {showSuccess ? (
                                <CardContent>
                                    <p className="text-pretty">Votre mot de passe a été réinitialisé avec succès. Connectez-vous avec votre nouveau mot de passe.</p>
                                </CardContent>
                            ) : (
                                <CardContent>
                                    <Form {...resetPasswordForm}>
                                        <form
                                            onSubmit={resetPasswordForm.handleSubmit(handleResetPassword)}
                                            className="space-y-4"
                                        >
                                            <FormItem>
                                                <FormLabel>Nouveau mot de passe</FormLabel>
                                                <FormControl>
                                                    <div className="flex items-center">
                                                        <Input
                                                            type={showPassword ? "text" : "password"}
                                                            autoComplete={"off"}
                                                            placeholder="Entrez votre nouveau mot de passe"
                                                            {...resetPasswordForm.register("newPassword")}
                                                        />
                                                        <Button
                                                            type="button"
                                                            className="h-9"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                        >
                                                            {showPassword ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                                                        </Button>
                                                    </div>
                                                </FormControl>
                                                <FormMessage>{resetPasswordForm.formState.errors.newPassword?.message?.toString()}</FormMessage>
                                            </FormItem>

                                            <FormItem>
                                                <FormLabel>Confirmer le mot de passe</FormLabel>
                                                <FormControl>
                                                    <div className="flex items-center">
                                                        <Input
                                                            type={showConfirmPassword ? "text" : "password"}
                                                            autoComplete="off"
                                                            placeholder="Confirmez votre mot de passe"
                                                            {...resetPasswordForm.register("confirmPassword")}
                                                        />
                                                        <Button
                                                            type="button"
                                                            className="h-9"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        >
                                                            {showConfirmPassword ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                                                        </Button>
                                                    </div>
                                                </FormControl>
                                                <FormMessage>{resetPasswordForm.formState.errors.confirmPassword?.message?.toString()}</FormMessage>
                                            </FormItem>

                                            <Button type="submit">Réinitialiser le mot de passe</Button>
                                        </form>
                                    </Form>
                                </CardContent>
                            )}
                        </Card>
                    </section>
                </div>
            </Container>
        </Layout>
    );
};